import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { AI_MODEL_AUTO, aiApi, type AiModelChoice } from "../api/aiApi";

/**
 * Every place AI can be run from. Each keeps its own model choice, so the
 * assistant can use a fast model while, say, obligation finding uses the
 * most capable one.
 */
export type AiSurface =
  | "assistant"
  | "contract.summary"
  | "contract.metadata"
  | "contract.obligations"
  | "obligation.summary"
  | "obligation.evidence"
  | "receivable.summary"
  | "template.draft"
  | "clause.compare";

interface AiModelState {
  /** Surface -> chosen model id; a missing surface runs on "auto". */
  bySurface: Partial<Record<AiSurface, string>>;
  setModel: (surface: AiSurface, model: string) => void;
  /** What the server offers; null until loaded. Not persisted: the administrator may change it. */
  choices: AiModelChoice[] | null;
  loadChoices: () => void;
  /** After the AI Models & Keys screen changes what is offered. */
  reloadChoices: () => void;
}

let loading: Promise<void> | null = null;

/** Remembered per browser: a preference, never a permission (the server checks every model). */
export const useAiModelStore = create<AiModelState>()(
  persist(
    (set) => ({
      bySurface: {},
      setModel: (surface, model) =>
        set((s) => ({ bySurface: { ...s.bySurface, [surface]: model || AI_MODEL_AUTO } })),
      choices: null,
      // Once per page load, shared by every picker. A failure just leaves "auto" as the only choice.
      loadChoices: () => {
        loading ??= Promise.resolve()
          .then(() => aiApi.models())
          .then((options) => {
            set({ choices: options?.models ?? [] });
          })
          .catch(() => {
            set({ choices: [] });
          });
      },
      reloadChoices: () => {
        loading = null;
        set({ choices: null });
      },
    }),
    {
      name: "lawsigna:ai-model:v1",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ bySurface: state.bySurface }),
    },
  ),
);
